import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useI18n } from "../../i18n";

const NAV = [
  { id: "stay", href: "#intro" },
  { id: "gallery", href: "#gallery" },
  { id: "experiences", href: "#experiences" },
  { id: "location", href: "#location" },
];

export default function Navbar() {
  const { t, lang, toggle } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const previous = document.body.style.overflow;
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = previous || "";
    }
    return () => {
      document.body.style.overflow = previous || "";
    };
  }, [open]);

  const drawer = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 md:hidden flex flex-col"
          style={{
            backgroundColor: "#FBF9F5",
            zIndex: 9999,
            height: "100vh",
            width: "100vw",
          }}
          data-testid="mobile-menu"
        >
          <div className="flex justify-between items-center px-6 py-5">
            <span className="font-serif text-2xl text-charcoal">La Caletta</span>
            <button
              onClick={() => setOpen(false)}
              data-testid="nav-menu-close"
              aria-label="Close menu"
            >
              <X size={22} strokeWidth={1.2} className="text-charcoal" />
            </button>
          </div>
          <div className="flex-1 flex flex-col justify-center px-8 gap-8">
            {NAV.map((n, i) => (
              <motion.a
                key={n.id}
                href={n.href}
                onClick={() => setOpen(false)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + i * 0.07, duration: 0.55 }}
                className="font-serif text-5xl text-charcoal"
                data-testid={`mobile-nav-${n.id}`}
              >
                {t.nav[n.id]}
              </motion.a>
            ))}
            <a
              href="#booking"
              onClick={() => setOpen(false)}
              className="mt-6 inline-flex w-fit border border-charcoal px-7 py-3 text-xs tracking-luxe uppercase"
              data-testid="mobile-nav-book"
            >
              {t.nav.book}
            </a>
          </div>
          <div className="px-8 pb-8 flex items-center justify-between text-[0.7rem] tracking-luxe uppercase text-charcoal-mute">
            <span>Sardegna · Mediterraneo</span>
            <button
              onClick={toggle}
              data-testid="mobile-lang-toggle"
              className="text-charcoal"
              aria-label="Toggle language"
            >
              <span className={lang === "it" ? "opacity-100" : "opacity-40"}>IT</span>
              <span className="opacity-40 mx-1">/</span>
              <span className={lang === "en" ? "opacity-100" : "opacity-40"}>EN</span>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <motion.header
        data-testid="site-navbar"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-[#FBF9F5]/80 border-b border-charcoal/5"
            : "bg-transparent"
        }`}
      >
        <div className="px-6 md:px-12 lg:px-20 py-5 md:py-6 flex items-center justify-between">
          <a href="#top" data-testid="brand-mark" className="flex flex-col leading-none">
            <span className={`font-serif text-2xl md:text-[1.6rem] tracking-tight transition-colors duration-500 ${scrolled ? "text-charcoal" : "text-ivory"}`}>
              La Caletta
            </span>
            <span className={`font-serif italic text-sm md:text-base -mt-0.5 transition-colors duration-500 ${scrolled ? "text-charcoal-mute" : "text-ivory/80"}`}>
              all'Alba
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) => (
              <a
                key={n.id}
                href={n.href}
                data-testid={`nav-link-${n.id}`}
                className={`link-elegant text-[0.72rem] tracking-luxe uppercase transition-colors duration-500 ${
                  scrolled ? "text-charcoal" : "text-ivory"
                }`}
              >
                {t.nav[n.id]}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4 md:gap-6">
            <button
              onClick={toggle}
              data-testid="lang-toggle"
              aria-label="Toggle language"
              className={`text-[0.72rem] tracking-luxe uppercase transition-colors duration-500 ${
                scrolled ? "text-charcoal" : "text-ivory"
              }`}
            >
              <span className={lang === "it" ? "opacity-100" : "opacity-40"}>IT</span>
              <span className="opacity-40 mx-1">/</span>
              <span className={lang === "en" ? "opacity-100" : "opacity-40"}>EN</span>
            </button>

            <a
              href="#booking"
              data-testid="nav-book-cta"
              className={`hidden md:inline-flex items-center px-6 py-2.5 text-[0.7rem] tracking-luxe uppercase border transition-all duration-500 ${
                scrolled
                  ? "border-charcoal text-charcoal hover:bg-charcoal hover:text-ivory"
                  : "border-ivory text-ivory hover:bg-ivory hover:text-charcoal"
              }`}
            >
              {t.nav.book}
            </a>

            <button
              className="md:hidden"
              onClick={() => setOpen(true)}
              data-testid="nav-menu-open"
              aria-label="Open menu"
            >
              <Menu className={scrolled ? "text-charcoal" : "text-ivory"} size={22} strokeWidth={1.2} />
            </button>
          </div>
        </div>
      </motion.header>

      {typeof document !== "undefined" ? createPortal(drawer, document.body) : drawer}
    </>
  );
}
